import ProductComparisonTable from "@app/components/Modules/ProductComparisonTable/ProductComparisonTable";
import { ReviewStoryApiResponse } from "@app/types/ServerCustomContext";
import { configSingleStory } from "@pub/config";
import {
  Button,
  ResponsiveImage,
  Typography,
} from "@sphtech/dmg-design-system";
import { ReactNode, useState } from "react";
import useSWR from "swr";

import styles from "./productStoryVerdict.module.css";

type Props = {
  id: string;
};

export default function ProductStoryVerdict({ id }: Props): ReactNode {
  const { Heading } = Typography;

  const { data, isLoading } = useSWR<ReviewStoryApiResponse>(
    (id && `/_plat/api/product/reviewById?id=${id}`) || "",
  );
  const [specsVisible, setSpecsVisible] = useState(false);

  if (isLoading || !data?.results) {
    return;
  }

  const { ratings, specifications, pros, cons, awards, shopButtons } =
    data.results;

  const productStoryRatingMap = configSingleStory.productStoryRatings || {};
  const validRatings =
    ratings?.filter((item) => item.key && item.key in productStoryRatingMap) ||
    [];
  const ratingData = validRatings.map(({ key, value }) => {
    return {
      label: productStoryRatingMap[key],
      value: value || "",
    };
  });

  const overallrating = ratings
    ?.filter((item) => item.key === "overall")
    .map((item) => item.value)
    .join(",");

  const toggleSpecs = () => {
    setSpecsVisible(!specsVisible);
  };

  const showSpecs = specifications && specifications.length > 0;
  const showProsCons = (pros && pros.length > 0) || (cons && cons.length > 0);
  const showRatings = !!overallrating || ratingData.length > 0;
  const showVertdict = showProsCons || showRatings;

  const svgCircle = (value: string) => {
    const percent = Number(value) * 10;
    return (
      <svg
        width="60"
        height="60"
        viewBox="0 0 60 60"
        className={styles.ratingCircle}
        style={{ "--progress": percent }}
      >
        <circle className={styles.bg}></circle>
        <circle className={styles.fg}></circle>
        <text x="30" y="33" textAnchor="middle" alignmentBaseline="middle">
          {value}
        </text>
      </svg>
    );
  };

  return (
    showVertdict && (
      <div className={styles.verdict}>
        <div className={styles.header}>
          <Heading.SubHeading>THE VERDICT</Heading.SubHeading>
        </div>
        {showRatings && (
          <div className={styles.awardsRatings}>
            <div className={styles.container}>
              <div className={styles.awards}>
                {awards?.map(
                  (image, index) =>
                    image.crops?.original && (
                      <div key={index} className={styles.image}>
                        <ResponsiveImage {...image.crops.original} />
                      </div>
                    ),
                )}
              </div>
              <div className={styles.overallRating}>{overallrating}</div>
            </div>
            <div className={styles.ratings}>
              {ratingData.map((item, index) => (
                <div key={index} className={styles.container}>
                  {svgCircle(item.value)}
                  <div className={styles.label}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {showSpecs && (
          <div className={styles.specs}>
            <button className={styles.title} onClick={toggleSpecs}>
              View specifications
            </button>
            {specsVisible && (
              <ProductComparisonTable
                hideProductNames={true}
                productSpecs={[{ name: "", specs: specifications }]}
              />
            )}
          </div>
        )}
        {showProsCons && (
          <div className={styles.prosCons}>
            <div className={styles.pros}>
              <div className={styles.header}>
                <Heading.SubHeading>THE GOOD</Heading.SubHeading>
              </div>
              <ul>
                {pros?.map((item, index) => (
                  <li key={index}>{item.value}</li>
                ))}
              </ul>
            </div>
            <div className={styles.cons}>
              <div className={styles.header}>
                <Heading.SubHeading>THE BAD</Heading.SubHeading>
              </div>
              <ul>
                {cons?.map((item, index) => (
                  <li key={index}>{item.value}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {shopButtons && (
          <div className={styles.shopButtons}>
            {shopButtons.map((item, index) => (
              <Button
                variant="primary"
                data-event-label={item.name}
                href={item.link}
                target="_blank"
                key={index}
              >
                {item.name}
              </Button>
            ))}
          </div>
        )}
      </div>
    )
  );
}
